import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

import { Client, ClientType } from 'src/app/api/models';
import { ClientsService } from 'src/app/api/services';

@Component({
  selector: 'app-clients-page',
  templateUrl: './clients-page.component.html',
  styleUrls: ['./clients-page.component.scss']
})
export class ClientsPageComponent implements OnInit {

  ClientType = ClientType;
  activeClientType: ClientType = ClientType.Company;

  clients$: Observable<Client[]>;
  displayedColumns: string[];

  searchControl = new FormControl('');

  private clientType$ = new BehaviorSubject<ClientType>(this.activeClientType);

  constructor(
    private clientsService: ClientsService,
    private router: Router
  ) {
    this.updateDisplayedColumns();
  }

  ngOnInit(): void {
    const search$ = this.searchControl.valueChanges.pipe(
      startWith(this.searchControl.value),
      debounceTime(300),
      distinctUntilChanged()
    );

    this.clients$ = combineLatest([this.clientType$, search$]).pipe(
      switchMap(([clientType, searchTerm]) =>
        this.clientsService.listClients({
          q: searchTerm,
          client_types: [clientType]
        })
      ),
      map(response => response.items)
    );
  }

  selectClientType(clientType: ClientType): void {
    this.activeClientType = clientType;
    this.updateDisplayedColumns();
    this.clientType$.next(clientType);
  }

  viewClient(client: Client): void {
    if (client.client_type === ClientType.Company) {
      this.router.navigate(['/clients', client.id]);
    } else {
      this.router.navigate(['/light-entrepreneur-clients', client.id]);
    }
  }

  // AI: Helper to get name from client.
  getName(client: Client): string {
    return client.name;
  }

  getClientIdValue(client: Client): string {
    if (client.client_type === ClientType.Company) {
      return client.business_id;
    }
    if (client.client_type === ClientType.LightEntrepreneur) {
      return client.personal_id;
    }
    return '';
  }

  // AI: Helper to get country from client.
  getCountry(client: Client): string {
    return client.country;
  }

  getClientIdHeader(): string {
    if (this.activeClientType === ClientType.Company) {
      return 'Business ID';
    }
    if (this.activeClientType === ClientType.LightEntrepreneur) {
      return 'Personal ID';
    }
    return 'ID';
  }

  // AI: Helper to get invoicing service company business id from client.
  getInvoicingServiceCompanyBusinessId(client: Client): string {
    if (client.client_type === ClientType.LightEntrepreneur) {
      return client.invoicing_service_company_business_id;
    }
    return '';
  }

  // AI: Helper to get responsible seller name from client.
  getResponsibleSellerName(client: Client): string {
    return client.responsible_seller?.name ?? '';
  }

  // AI: Helper to get created_at from client.
  getCreatedAt(client: Client): string {
    return client.created_at;
  }

  private updateDisplayedColumns(): void {
    if (this.activeClientType === ClientType.Company) {
      this.displayedColumns = ['name', 'id_number', 'country', 'responsible_seller', 'created_at'];
    } else {
      this.displayedColumns = ['name', 'id_number', 'invoicing_service_company_business_id', 'country', 'responsible_seller', 'created_at'];
    }
  }
}
